import { Box, IconButton, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export const CustomerAccessEditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$fillHeight",
})(({ theme, $fillHeight }) => ({
  display: $fillHeight ? "flex" : "block",
  flexDirection: $fillHeight ? "column" : undefined,
  flex: $fillHeight ? 1 : undefined,
  minHeight: $fillHeight ? 0 : undefined,
  overflow: $fillHeight ? "hidden" : undefined,
  marginTop: theme.spacing(2),
}));

export const CustomerAccessEditorHeader = styled(Stack)(({ theme }) => ({
  flexShrink: 0,
  marginBottom: theme.spacing(1),
}));

export const CustomerAccessDescription = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

export const CustomerAccessBorderedPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$fillHeight",
})(({ theme, $fillHeight }) => ({
  display: $fillHeight ? "flex" : "block",
  flexDirection: $fillHeight ? "column" : undefined,
  flex: $fillHeight ? 1 : undefined,
  minHeight: $fillHeight ? 0 : undefined,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  overflow: "hidden",
}));

export const CustomerAccessRowsScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  // Room for outlined input labels that sit above the field border (first row).
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
}));

export const CustomerAccessColumnHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  flexShrink: 0,
  gap: theme.spacing(2),
  padding: theme.spacing(0.75, 1.5),
}));

export const CUSTOMER_ACCESS_CUSTOMER_COLUMN_WIDTH = 190;

export const CustomerAccessColumnHeaderLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.secondary,
}));

export const CustomerAccessCustomerHeaderCell = styled(Box)(({ theme }) => ({
  flex: `0 0 ${CUSTOMER_ACCESS_CUSTOMER_COLUMN_WIDTH}px`,
  maxWidth: CUSTOMER_ACCESS_CUSTOMER_COLUMN_WIDTH,
  minWidth: theme.spacing(16),
}));

export const CustomerAccessRolesHeaderCell = styled(Box)({
  flex: "1 1 0",
  minWidth: 0,
});

export const CustomerAccessRemoveHeaderCell = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: theme.spacing(6),
  textAlign: "right",
}));

export const ACCESS_STATUS_COLUMN_MIN_WIDTH = 88;

export const CustomerAccessStatusHeaderCell = styled(Box)({
  flex: "0 0 auto",
  minWidth: ACCESS_STATUS_COLUMN_MIN_WIDTH,
  width: ACCESS_STATUS_COLUMN_MIN_WIDTH,
  maxWidth: "100%",
});

export const CustomerAccessStatusHeaderLabel = styled(CustomerAccessColumnHeaderLabel)({
  flex: "none",
});

export const CustomerAccessRows = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$inScrollArea",
})(({ theme, $inScrollArea }) => ({
  gap: 0,
  paddingTop: $inScrollArea ? 0 : theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
}));

export const CustomerAccessRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75, 1.5, 0.5),
  marginBottom: theme.spacing(0.5),
}));

export const CustomerAccessRowInner = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(2),
}));

export const CustomerAccessCustomerFieldCell = styled(Box)(({ theme }) => ({
  flex: `0 0 ${CUSTOMER_ACCESS_CUSTOMER_COLUMN_WIDTH}px`,
  maxWidth: CUSTOMER_ACCESS_CUSTOMER_COLUMN_WIDTH,
  minWidth: theme.spacing(16),
  alignSelf: "flex-start",
  overflow: "visible",
  "& > *": {
    width: "100%",
    overflow: "visible",
  },
}));

export const CustomerAccessRolesFieldCell = styled(Box)({
  flex: "1 1 0",
  minWidth: 0,
  alignSelf: "flex-start",
  overflow: "visible",
  "& > *": {
    width: "100%",
    overflow: "visible",
  },
});

/** Status chip only as wide as its label; Customer + Roles columns take remaining flex space. */
export const CustomerAccessStatusFieldCell = styled(Box)({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  alignSelf: "flex-start",
  minWidth: ACCESS_STATUS_COLUMN_MIN_WIDTH,
  width: ACCESS_STATUS_COLUMN_MIN_WIDTH,
  maxWidth: "100%",
});

/** Reserves the same box as AccessStatusChip when accessId is not set yet. */
export const CustomerAccessStatusPlaceholder = styled(Box)({
  height: 28,
  width: "100%",
  flexShrink: 0,
});

export const CustomerAccessRemoveCell = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: theme.spacing(6),
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  alignSelf: "flex-start",
  paddingTop: theme.spacing(0.75),
}));
export const CustomerAccessAccessStatusCell = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 100,
  textAlign: "left",
}));
export const CustomerAccessRemoveIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.25),
}));

export const CustomerAccessDeactivatedWarning = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  color: theme.palette.warning.main,
  fontSize: theme.typography.caption.fontSize,
}));
